import { LOCALES, type Locale } from '@aysar/contracts';

/** A catalog is an arbitrarily nested tree of strings. */
export interface Catalog {
  [key: string]: string | Catalog;
}

/** One namespace's ar + en trees. Both locales are mandatory — U8 has no partial state. */
export type LocaleCatalogs = Record<Locale, Catalog>;

export interface CatalogDiff {
  /** Paths present in the first catalog and absent from the second. */
  missingInSecond: string[];
  /** Paths present in the second catalog and absent from the first. */
  missingInFirst: string[];
}

/**
 * The CLDR plural categories i18next encodes as a key suffix (`items_two`).
 *
 * These exist because U8's "identical key trees" cannot be taken literally for a
 * counted string. Arabic resolves six categories, English two, so an Arabic
 * catalog that carried the same keys as the English one would be MISSING the
 * dual — "2 محدد" instead of "عنصران محددان". Parity is therefore compared over
 * plural FAMILIES (the key with its category stripped), and the per-locale
 * category set is checked separately, against `Intl.PluralRules` rather than
 * against the other locale.
 */
const PLURAL_CATEGORIES = ['zero', 'one', 'two', 'few', 'many', 'other'] as const;

const PLURAL_SUFFIX = new RegExp(`_(${PLURAL_CATEGORIES.join('|')})$`);

/**
 * `_zero` is i18next's **exact-count** key, not only a CLDR category.
 *
 * For `count === 0` i18next looks up `key_zero` first **in every language**,
 * falling through to the category the locale's rules select when it is absent
 * (verified against i18next 26 on this tree: `en` with `m_zero`/`m_one`/`m_other`
 * answers `m_zero` at 0). So a `_zero` in English is live copy, and treating it
 * as a category the locale "never selects" would forbid the one thing a delete
 * warning most needs to say — *nobody has answered this yet* — pushing it into a
 * sentence built by concatenating two translated fragments instead.
 *
 * It stays **optional** outside the locales whose rules require it: an English
 * family without one falls back to `_other`, which reads acceptably ("0 members
 * have answered this"). Every other category is still checked, because `_two` in
 * English really is copy nothing will ever render.
 */
const EXACT_COUNT_CATEGORY = 'zero';

/** `items_two` → `{ family: 'items', category: 'two' }`; a plain key → null. */
function splitPlural(path: string): { family: string; category: string } | null {
  const match = PLURAL_SUFFIX.exec(path);
  if (match === null) return null;
  return { family: path.slice(0, match.index), category: match[1] as string };
}

/**
 * The key paths of a catalog with every plural category collapsed to its family.
 *
 * Exported because the orphan check compares over the same unit: code calls
 * `t('bulk.selected', { count })`, so `bulk.selected_two` is reachable copy and
 * looking for its literal spelling would report six orphans per plural.
 */
export function collectFamilies(catalog: Catalog): string[] {
  return [...new Set(collectKeys(catalog).map((path) => splitPlural(path)?.family ?? path))].sort();
}

/** The categories a locale's plural rules actually require, e.g. ar → 6, en → 2. */
function requiredCategories(locale: Locale): string[] {
  return [...new Intl.PluralRules(locale).resolvedOptions().pluralCategories].sort();
}

/** family → the categories this catalog supplies for it (empty when not a plural). */
function pluralFamilies(catalog: Catalog): Map<string, Set<string>> {
  const families = new Map<string, Set<string>>();

  for (const path of collectKeys(catalog)) {
    const split = splitPlural(path);
    if (split === null) continue;
    const categories = families.get(split.family) ?? new Set<string>();
    categories.add(split.category);
    families.set(split.family, categories);
  }

  return families;
}

/**
 * Flattens a catalog to its sorted dotted key paths.
 *
 * Carried over verbatim from the P0 `translate.ts` this module replaces: the
 * lookup moved to i18next, but the parity check is ours and did not need to.
 */
export function collectKeys(catalog: Catalog, prefix = ''): string[] {
  return Object.entries(catalog)
    .flatMap(([key, value]) => {
      const path = prefix === '' ? key : `${prefix}.${key}`;
      return typeof value === 'string' ? [path] : collectKeys(value, path);
    })
    .sort();
}

/**
 * The keys each catalog has that the other does not — both directions.
 *
 * Compared over plural families, so `items_two` present only in Arabic is not a
 * hole; a missing category is reported by `assertCatalogParity` instead, against
 * the locale's own rules.
 */
export function diffCatalogs(first: Catalog, second: Catalog): CatalogDiff {
  const firstKeys = new Set(collectFamilies(first));
  const secondKeys = new Set(collectFamilies(second));

  return {
    missingInSecond: [...firstKeys].filter((key) => !secondKeys.has(key)).sort(),
    missingInFirst: [...secondKeys].filter((key) => !firstKeys.has(key)).sort(),
  };
}

function blankKeys(catalog: Catalog): string[] {
  return collectKeys(catalog).filter((path) => {
    const value = path.split('.').reduce<string | Catalog | undefined>((node, segment) => {
      if (node === undefined || typeof node === 'string') return undefined;
      return node[segment];
    }, catalog);
    return typeof value === 'string' && value.trim().length === 0;
  });
}

/**
 * U8's parity law as an assertion. Three things are checked, and the second is
 * the one that a naive reading of "identical key trees" gets wrong:
 *
 *  1. Every key FAMILY in `ar.json` exists in `en.json` and vice versa.
 *  2. Every plural family supplies exactly the categories THAT locale's rules
 *     require — six for Arabic, two for English — plus an optional `_zero`,
 *     which is i18next's exact-count key rather than a category (see
 *     `EXACT_COUNT_CATEGORY`). Comparing the two locales' plural keys to each
 *     other would demand an Arabic dual in English (absurd) or forbid it in
 *     Arabic (wrong); comparing each against `Intl.PluralRules` is the check
 *     that actually means something.
 *  3. Neither side ships a blank. An empty string renders as nothing, so a
 *     button with no label reads as a broken feature rather than as an
 *     untranslated one — the failure a missing key, which i18next surfaces as
 *     the key itself, deliberately avoids.
 *
 * Throws rather than returning a result, so a catalog spec is one line and a
 * broken catalog fails at import time (see `registerCatalog`).
 */
export function assertCatalogParity(namespace: string, catalogs: LocaleCatalogs): void {
  const [primary, secondary] = LOCALES;
  const diff = diffCatalogs(catalogs[primary], catalogs[secondary]);
  const problems: string[] = [];

  if (diff.missingInSecond.length > 0) {
    problems.push(`missing from ${secondary}: ${diff.missingInSecond.join(', ')}`);
  }
  if (diff.missingInFirst.length > 0) {
    problems.push(`missing from ${primary}: ${diff.missingInFirst.join(', ')}`);
  }

  for (const locale of LOCALES) {
    const required = requiredCategories(locale);

    for (const [family, supplied] of pluralFamilies(catalogs[locale])) {
      const missing = required.filter((category) => !supplied.has(category));
      const extra = [...supplied]
        .filter((category) => !required.includes(category) && category !== EXACT_COUNT_CATEGORY)
        .sort();

      if (missing.length > 0) {
        problems.push(
          `${locale} plural "${family}" is missing ${missing.map((c) => `${family}_${c}`).join(', ')}`,
        );
      }
      if (extra.length > 0) {
        problems.push(
          `${locale} plural "${family}" declares ${extra
            .map((c) => `${family}_${c}`)
            .join(', ')}, which ${locale} never selects`,
        );
      }
    }

    const blanks = blankKeys(catalogs[locale]);
    if (blanks.length > 0) {
      problems.push(`empty in ${locale}: ${blanks.join(', ')}`);
    }
  }

  if (problems.length > 0) {
    throw new Error(`i18n parity broken in namespace "${namespace}" — ${problems.join('; ')}.`);
  }
}
